import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Form, FormikProps } from 'formik';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { FocusFirstInvalidField } from '../../_core/Formik/focus-first-invalid-field.component';
import { FieldError } from '../../_core/Formik/field-error.component';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { ZoomableImage } from '../../_core/_ui/zoomable-image.component';
import { pluralize } from '../../../lib/helpers/text.helpers';
import { useCompanySport, useCompanySportTerminology } from '../../../lib/stores/company.store';
import { SPORT_HAS_GOALIES } from '../../../lib/constants';
import { SportTerminology } from '../../../lib/types';
import {
  buildTemplateBandOptions,
  groupTemplatesByBucket,
  resolveInitialBucket,
  sortAgeGroupBuckets,
  templateBandOptionValue,
} from '../../../lib/helpers/format-template.helpers';
import { ISuggestedTemplate } from '../../../lib/types/format-template.types';
import { FormatTemplatePills } from '../format-template-pills.component';
import { messages } from '../format-template.messages';
import { IUseFormatTemplateFields } from './use-template-modal.types';

const FORM_ID = 'use-format-template-form';

interface IUseTemplateModalProps {
  open: boolean;
  onClose: () => void;
  templates: IFormatTemplate[];
  templatesLoading: boolean;
  selectedTemplate: IFormatTemplate | null;
  // The age group's own bucket, used to open the picker on the templates built for it.
  ageGroupBucket: string | null;
  recommended: ISuggestedTemplate | null;
  preview: IFormatTemplatePreview | null;
  previewError: boolean;
  form: FormikProps<IUseFormatTemplateFields>;
}

export const UseTemplateModal: React.FC<IUseTemplateModalProps> = ({
  open,
  onClose,
  templates,
  templatesLoading,
  selectedTemplate,
  ageGroupBucket,
  recommended,
  preview,
  previewError,
  form,
}) => {
  const { values, errors, status, isSubmitting, submitCount, setFieldValue } = form;
  const termLookup = useCompanySportTerminology();
  const hasGoalies = SPORT_HAS_GOALIES[useCompanySport()];

  const bucketMap = useMemo(() => groupTemplatesByBucket(templates), [templates]);
  const buckets = useMemo(() => sortAgeGroupBuckets(Array.from(bucketMap.keys())), [bucketMap]);
  const bucketTemplates = useMemo(
    () => (values.bucket ? bucketMap.get(values.bucket) ?? [] : []),
    [bucketMap, values.bucket],
  );
  // Each band of a banded template is its own applyable option; a flat template stays one option.
  const options = useMemo(() => buildTemplateBandOptions(bucketTemplates), [bucketTemplates]);

  // Open on the recommendation, or failing that the age group's own bucket, once the templates
  // land. The ref latches so a later refetch never pulls the coach off what they picked.
  const hasSeededRef = useRef(false);
  useEffect(() => {
    if (hasSeededRef.current || templates.length === 0) return;
    hasSeededRef.current = true;
    if (values.bucket || values.templateId) return;
    const initialBucket = resolveInitialBucket(templates, recommended?.template ?? null, ageGroupBucket);
    if (initialBucket) setFieldValue('bucket', initialBucket);
    if (recommended) {
      setFieldValue('templateId', recommended.template._id);
      setFieldValue('bandKey', recommended.bandKey);
    }
  }, [templates, recommended, ageGroupBucket, values.bucket, values.templateId, setFieldValue]);

  // Not memoized: a lookup over the handful of bands one template declares, no hook dep array.
  const activeBand = selectedTemplate?.bands?.find(b => b.key === values.bandKey) ?? null;
  const previewImageUrl = activeBand?.imageUrl || selectedTemplate?.imageUrl;
  // Not memoized: a cheap string build over two fields, not fed to any hook dep array.
  const selectedOptionValue = values.templateId
    ? templateBandOptionValue(values.templateId, values.bandKey)
    : '';
  const isRecommendedSelection = !!recommended && !!selectedTemplate
    && recommended.template._id === selectedTemplate._id && recommended.bandKey === values.bandKey;

  const handleBucketChange = useCallback((nextBucket: string) => {
    setFieldValue('bucket', nextBucket);
    const stillListed = (bucketMap.get(nextBucket) ?? []).some(t => t._id === values.templateId);
    if (!stillListed) {
      setFieldValue('templateId', null);
      setFieldValue('bandKey', null);
      setFieldValue('previewStageCount', null);
    }
  }, [bucketMap, values.templateId, setFieldValue]);

  const handleOptionChange = useCallback((value: string) => {
    const option = options.find(o => o.value === value);
    if (!option) return;
    setFieldValue('templateId', option.templateId);
    setFieldValue('bandKey', option.bandKey);
    // The count still describes the previous selection until the re-run preview resolves.
    setFieldValue('previewStageCount', null);
  }, [options, setFieldValue]);

  const selectionError = submitCount > 0 && typeof errors.templateId === 'string' ? errors.templateId : '';
  const showStageCountError = !!errors.previewStageCount && (values.previewStageCount === 0 || submitCount > 0);
  // Not memoized: a cheap ternary over a nullish read and a length, not fed to any hook dep array.
  const templateHint = !values.bucket
    ? messages.selectAgeGroupFirst
    : (bucketTemplates.length === 0 ? messages.noTemplatesForBucket : null);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {/* Form lives inside the Dialog so the portal-rendered fields share its id for focus targeting. */}
      <Form id={FORM_ID}>
        <FocusFirstInvalidField formId={FORM_ID} />
        <DialogTitle><IntlMsg {...messages.applyTitle} /></DialogTitle>
        <DialogContent>
          {templatesLoading && templates.length === 0 && (
            <Typography><IntlMsg {...messages.loadingTemplates} /></Typography>
          )}
          {!templatesLoading && templates.length === 0 && (
            <Typography color="textSecondary"><IntlMsg {...messages.noTemplatesForAssociation} /></Typography>
          )}
          {templates.length > 0 && (
            <>
              <TextField
                select
                name="bucket"
                label={<IntlMsg {...messages.ageGroupLabel} />}
                value={values.bucket ?? ''}
                onChange={e => handleBucketChange(e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                disabled={isSubmitting}
                style={{ marginBottom: 16 }}
              >
                {buckets.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
              </TextField>
              <TextField
                select
                name="templateId"
                label={<IntlMsg {...messages.templateLabel} />}
                value={selectedOptionValue}
                onChange={e => handleOptionChange(e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                disabled={isSubmitting || !values.bucket}
                error={!!selectionError}
                helperText={selectionError
                  ? <FieldError error={selectionError} catalog={messages} />
                  : templateHint && <IntlMsg {...templateHint} />}
              >
                {options.map(o => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                    {recommended?.template._id === o.templateId && recommended?.bandKey === o.bandKey
                      && <>{' ('}<IntlMsg {...messages.recommended} />{')'}</>}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}

          {selectedTemplate && (
            <Box mt={2}>
              <ZoomableImage
                src={previewImageUrl}
                alt={selectedTemplate.name}
                minHeight={320}
                maxHeight={500}
                mb={1}
              />
              <FormatTemplatePills
                template={selectedTemplate}
                band={activeBand}
                isRecommended={isRecommendedSelection}
                totalAssessmentSessions={preview?.totalAssessmentSessions}
              />
              {preview?.nonGoalieCount != null && (
                <Box mt={1}>
                  <Typography variant="body2" color="textSecondary">
                    {hasGoalies ? (
                      <IntlMsg
                        {...messages.rosterCountWithGoalies}
                        values={{
                          nonGoalieCount: preview.nonGoalieCount,
                          goalieCount: preview.goalieCount ?? 0,
                          skaterWord: pluralize(preview.nonGoalieCount, termLookup[SportTerminology.SKATER].toLowerCase()),
                          goalieWord: pluralize(preview.goalieCount ?? 0, termLookup[SportTerminology.GOALIE].toLowerCase()),
                        }}
                      />
                    ) : (
                      <IntlMsg
                        {...messages.rosterCount}
                        values={{
                          count: preview.nonGoalieCount,
                          skaterWord: pluralize(preview.nonGoalieCount, termLookup[SportTerminology.SKATER].toLowerCase()),
                        }}
                      />
                    )}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* The preview still runs headless — it feeds the stage-count guard and roster count —
              so its failure and the guard's error keep an on-screen surface. */}
          {selectedTemplate && (previewError || showStageCountError) && (
            <Box mt={2}>
              {previewError ? (
                <FormHelperText error><IntlMsg {...messages.previewError} /></FormHelperText>
              ) : (
                <FormHelperText error>
                  <FieldError error={errors.previewStageCount} catalog={messages} />
                </FormHelperText>
              )}
            </Box>
          )}

          {selectedTemplate && (
            <Box mt={3}>
              <Typography variant="subtitle2" gutterBottom><IntlMsg {...messages.applyModeTitle} /></Typography>
              <RadioGroup
                value={values.applyMode}
                onChange={e => setFieldValue('applyMode', e.target.value as 'APPEND' | 'REPLACE')}
              >
                <FormControlLabel
                  value="REPLACE"
                  control={<Radio />}
                  label={<IntlMsg {...messages.replaceOption} />}
                />
                <FormControlLabel
                  value="APPEND"
                  control={<Radio />}
                  label={<IntlMsg {...messages.appendOption} />}
                />
              </RadioGroup>
            </Box>
          )}
          {status && (
            <Box mt={2}>
              <FormHelperText error>{status}</FormHelperText>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}><IntlMsg {...messages.cancel} /></Button>
          <Button type="submit" color="primary" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <CircularProgress size={18} style={{ marginRight: 8 }} />
                <IntlMsg {...messages.applying} />
              </>
            ) : <IntlMsg {...messages.applyTemplate} />}
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};
